import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { opsApi } from '@/api/ops';
import type { ImportKeyView } from '@/api/opsTypes';
import { PERMISSIONS } from '@/api/types';
import { Segmented, Select } from '@/components/Select';
import { StatusChip } from '@/components/ops/Badges';
import { TextField } from '@/components/ops/Forms';
import { PageHeader, Section } from '@/components/ops/Layout';
import { ConfirmDialog, Modal, RevealOnce } from '@/components/ops/Modal';
import { RequirePermission } from '@/components/ops/PermissionGate';
import { MutationError, QueryState } from '@/components/ops/Problem';
import { formatTimestamp } from '@/ops/format';
import { REPLICA } from '@/ops/live';

type Scope = 'hosts' | 'accounts' | 'all' | 'tasks';

/**
 * The collectors' credentials (`cpik_…`). Shown ONCE at issue, stored as SHA-256 by the console, and
 * revocable. Never a platform internal key, never an admin login.
 */
export function ImportKeysPage() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<{ key: string; label: string; scope: string } | null>(null);
  const [revoking, setRevoking] = useState<ImportKeyView | null>(null);

  const keys = useQuery({ queryKey: ['ops', 'import-keys'], queryFn: opsApi.importKeys.list, ...REPLICA });

  const revoke = useMutation({
    mutationFn: (id: string) => opsApi.importKeys.revoke(id),
    onSuccess: () => {
      setRevoking(null);
      queryClient.invalidateQueries({ queryKey: ['ops', 'import-keys'] });
    },
  });

  return (
    <RequirePermission permission={PERMISSIONS.environmentsManage} what="Import keys">
      <PageHeader
        title="Import keys"
        subtitle="Credentials for tools/collect-host.sh and tools/collect-tenants.sh (CONTROLPLANE_IMPORT_KEY)."
        actions={<button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>Issue key</button>}
      />

      <QueryState query={keys}>
        {(data) => (
          <Section title="Keys" meta={`${data.keys.filter((k) => !k.revoked_at).length} active`} flush>
            {data.keys.length === 0 ? (
              <div className="empty-state t-body">No import keys. Issue one for each host's collectors.</div>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>Key</th><th>Scope</th><th>Environment</th><th>Status</th><th>Created</th><th>Last used</th><th aria-label="Actions" /></tr>
                  </thead>
                  <tbody>
                    {data.keys.map((k) => (
                      <tr key={k.id} className={k.revoked_at ? 'row-muted' : undefined}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{k.label}</div>
                          <div className="t-caption mono muted">{k.display}</div>
                        </td>
                        <td className="mono">{k.scope}</td>
                        <td className="t-caption">
                          {k.environment
                            ? <span className="mono">{k.environment}</span>
                            : k.scope === 'hosts' ? <span className="subtle">any (hosts only)</span> : <span className="note-warning">not bound — issue a new key</span>}
                        </td>
                        <td>
                          {k.revoked_at ? <StatusChip status="revoked" title={formatTimestamp(k.revoked_at)} /> : <StatusChip status="active" />}
                        </td>
                        <td className="t-caption">{formatTimestamp(k.created_at)}<div className="muted">{k.created_by ?? ''}</div></td>
                        <td className="t-caption">{k.last_used_at ? formatTimestamp(k.last_used_at) : <span className="subtle">never</span>}</td>
                        <td>
                          {!k.revoked_at && (
                            <button type="button" className="btn btn-ghost btn-sm is-danger" onClick={() => setRevoking(k)}>Revoke</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        )}
      </QueryState>

      {creating && (
        <IssueKeyForm
          onClose={() => setCreating(false)}
          onIssued={(key, label, scope) => {
            setCreating(false);
            setIssued({ key, label, scope });
            queryClient.invalidateQueries({ queryKey: ['ops', 'import-keys'] });
          }}
        />
      )}

      {issued && (
        <RevealOnce title={`Import key — ${issued.label}`} what="import key" secret={issued.key} onDismiss={() => setIssued(null)}>
          <p className="t-caption muted" style={{ margin: 0 }}>
            Scope <span className="mono">{issued.scope}</span>. Put it in the collector's environment as{' '}
            <span className="mono">CONTROLPLANE_IMPORT_KEY</span> (anware-deploy passes it on stdin, never on argv).
          </p>
        </RevealOnce>
      )}

      {revoking && (
        <ConfirmDialog
          title={`Revoke “${revoking.label}”?`}
          confirmLabel="Revoke key"
          destructive
          pending={revoke.isPending}
          onConfirm={() => revoke.mutate(revoking.id)}
          onCancel={() => {
            setRevoking(null);
            revoke.reset();
          }}
        >
          <p className="t-body" style={{ margin: 0 }}>
            Collectors using <span className="mono">{revoking.display}</span> stop being accepted within 60 seconds on every
            replica. Issue a replacement first if a host still needs to report.
          </p>
          <MutationError error={revoke.error} />
        </ConfirmDialog>
      )}
    </RequirePermission>
  );
}

function IssueKeyForm({ onClose, onIssued }: { onClose: () => void; onIssued: (key: string, label: string, scope: string) => void }) {
  const [label, setLabel] = useState('');
  const [scope, setScope] = useState<Scope>('all');
  const [environment, setEnvironment] = useState('');
  const environments = useQuery({ queryKey: ['ops', 'environments'], queryFn: () => opsApi.environments.list() });
  const envOptions = (environments.data?.environments ?? []).map((e) => ({ value: e.key, label: `${e.label} (${e.key})` }));
  // A key that imports tenants speaks for ONE environment; with a single environment that is the only answer.
  const importsTenants = scope !== 'hosts';
  const chosenEnvironment = environment || (envOptions.length === 1 ? envOptions[0]!.value : '');

  // gcTime 0 + reset(): the plaintext moves straight into the reveal dialog's state and is dropped from
  // the mutation cache, so no cache entry keeps a copy after the dialog closes.
  const issue = useMutation({
    mutationFn: () => opsApi.importKeys.create(label.trim(), scope, importsTenants ? chosenEnvironment : undefined),
    gcTime: 0,
  });

  return (
    <Modal
      title="Issue an import key"
      onClose={issue.isPending ? undefined : onClose}
      dismissible={!issue.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={issue.isPending}>Cancel</button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={label.trim().length === 0 || (importsTenants && !chosenEnvironment) || issue.isPending}
            onClick={() =>
              issue.mutate(undefined, {
                onSuccess: (result) => {
                  const plaintext = result.import_key;
                  issue.reset();
                  onIssued(plaintext, result.key.label, result.key.scope);
                },
              })}
          >
            {issue.isPending ? 'Issuing…' : 'Issue key'}
          </button>
        </>
      }
    >
      <TextField label="Label" value={label} onChange={setLabel} required placeholder="tavi host collectors" />
      <Segmented
        label="Scope"
        value={scope}
        onChange={setScope}
        options={[
          { value: 'hosts', label: 'hosts' },
          { value: 'accounts', label: 'accounts' },
          { value: 'all', label: 'all' },
          { value: 'tasks', label: 'tasks' },
        ]}
      />
      <p className="t-caption muted" style={{ margin: 0 }}>
        <span className="mono">hosts</span> → POST /hosts/import · <span className="mono">accounts</span> → POST /accounts/import and
        /services/import · <span className="mono">all</span> → both · <span className="mono">tasks</span> → the server's task runner
        (tenant backups before a deletion) — never part of <span className="mono">all</span>; give it only to tools/host-tasks.sh.
      </p>
      {importsTenants && (
        <Select
          label="Environment"
          value={chosenEnvironment}
          onChange={setEnvironment}
          options={envOptions}
          placeholder="Choose the environment this key reports for"
          hint="The key can only report tenants of this environment — a collector of another stack can never change them."
        />
      )}
      <MutationError error={issue.error} />
    </Modal>
  );
}
